// 17 real public SHAILY24 repositories. Source of truth:
// REPO_AUDIT/CONTENT_REFRESH.md §4 + PROFILE_COPY.md + HOSTING.md
// (repo-verified 2026-05-17). Live set = original 8 demos + cardauth + mbridge +
// cea fully live, kbrag labelled "retrieval demo, chat disabled", btxn
// labelled "frontend preview" => 13 live. citizens.shaily.dev is retired and
// is referenced nowhere. Only metrics #1 (~515,738 reviews), #2 (RF 77.5 /
// XGB 78.5 / NN 76.5) and the optional CRS <200ms p95 (cached) appear. Zero
// banned career metrics. Each detail = problem -> approach -> tradeoff + an
// auth/security note (sharpening adjustment 2).

const PROJECTS = [
  // Band 1, Live end-to-end. The original 8 plus cardauth / mbridge / cea /
  // kbrag / btxn => 13 live, sorted to the top by BAND_ORDER.
  {
    name: 'credit-risk-system',
    desc: 'Full-stack ML risk API. A scikit-learn model behind a JWT-auth FastAPI, <200ms p95 on the cached path.',
    stack: 'FastAPI · React 18 · PostgreSQL · Redis · Docker',
    type: 'Live', band: 'Live',
    live: 'https://crs.shaily.dev',
    repo: 'SHAILY24/credit-risk-system',
    role: 'Solo build',
    problem:
      'You can read about a credit model on a slide. You cannot poke at it. I wanted reviewers to be able to poke at it.',
    approach:
      'scikit-learn model, FastAPI in front of it, React 18 UI. PostgreSQL holds the application data and Redis caches the hot read path. The whole thing is Docker Compose so it behaves the same on my laptop and on the box.',
    tradeoff:
      'The cache is what gets me the <200ms p95 read. The cost is that a score can be a few seconds stale. Fine for a demo. A real lender would invalidate the cache per applicant instead.',
    security:
      'Everything is behind JWT auth with short-lived tokens. The model is never reachable directly. Inputs get validated at the API boundary before the estimator ever sees them.',
  },
  {
    name: 'shelf-bookstore',
    desc: 'E-commerce with a real Stripe checkout. Webhook signatures verified, JWT sessions, bcrypt on passwords.',
    stack: 'React · TypeScript · FastAPI · Stripe',
    type: 'Live', band: 'Live',
    live: 'https://shelf.shaily.dev',
    repo: 'SHAILY24/shelf-bookstore',
    role: 'Solo build',
    problem:
      'A lot of demo storefronts have a "Buy" button that just flips a boolean. I wanted the payment path wired correctly, end to end.',
    approach:
      'React and TypeScript on the front, FastAPI behind it, a real Stripe checkout session. The order state machine does not advance until the Stripe webhook confirms the payment actually happened.',
    tradeoff:
      'It runs against Stripe test mode, so it is free and safe but cannot show a real settled charge. The integration code is the production shape either way.',
    security:
      'Webhook payloads are checked against the Stripe signing secret before anything trusts them. Sessions are JWTs. Passwords are bcrypt-hashed and never stored or logged in clear.',
  },
  {
    name: 'news-sentiment-intelligence',
    desc: 'Multi-model news NLP. FinBERT, VADER and TextBlob run together, with a Prophet forecast on the series.',
    stack: 'React · Flask · Redis · PostgreSQL',
    type: 'Live', band: 'Live',
    live: 'https://news.shaily.dev',
    repo: 'SHAILY24/news-sentiment-intelligence',
    role: 'Solo build',
    problem:
      'One sentiment model on financial headlines is brittle. When models disagree on a headline, that disagreement is the signal. It should be visible, not averaged away.',
    approach:
      'Pull RSS and news feeds, run FinBERT, VADER and TextBlob over each article in parallel, write the results to PostgreSQL and cache the hot reads in Redis. Prophet forecasts the aggregate series on top.',
    tradeoff:
      'Three models per article is more compute than one. Worth it. The point is to surface the headlines the models cannot agree on, not to hide them under a single number.',
    security:
      'Ingestion is read-only and there are no user accounts. Feed fetches are scoped to a known source list, so nobody can point the pipeline at an arbitrary URL.',
  },
  {
    name: 'interactive-banking-analytics-dashboard',
    desc: 'Ask a question in plain English, get an explainable answer. RF 77.5% / XGBoost 78.5% / NN 76.5%, SHAP, SMOTE.',
    stack: 'Flask · D3.js · scikit-learn',
    type: 'Live', band: 'Live',
    live: 'https://ibad.shaily.dev',
    repo: 'SHAILY24/interactive-banking-analytics-dashboard',
    role: 'Solo build',
    problem:
      'A risk model nobody can question is a liability. An analyst should be able to type a question in plain English and then see why the model answered the way it did.',
    approach:
      'Flask back end with a natural-language query layer over the banking data, D3.js dashboard on top. The ensemble is Random Forest at 77.5%, XGBoost at 78.5% and a Neural Network at 76.5%. SHAP explains each score and SMOTE handles the class imbalance.',
    tradeoff:
      'SMOTE lifts recall on the rare class, but it does it by synthesizing rows the model never actually saw. So SHAP rides along with every score. A reviewer can sanity-check the reasoning instead of trusting the number.',
    security:
      'The NL layer can only run a vetted set of query templates. A typed question cannot become arbitrary database access.',
  },
  {
    name: 'hotel-reviews-analysis',
    desc: 'Review NLP and segmentation over ~515,738 hotel reviews. VADER for sentiment, TF-IDF, K-Means.',
    stack: 'Python · pandas · scikit-learn · NLTK',
    type: 'Live', band: 'Live',
    live: 'https://hotel-reviews-analysis.shaily.dev',
    repo: 'SHAILY24/hotel-reviews-analysis',
    role: 'Solo build',
    problem:
      'Half a million free-text reviews do a business no good as a pile of text. They have to be scored and grouped into segments someone can actually act on.',
    approach:
      'pandas pipeline over ~515,738 reviews. VADER for sentiment, TF-IDF features, K-Means for the customer segments, and a multi-panel dashboard that turns it into something a business reads.',
    tradeoff:
      'K-Means makes you pick the cluster count up front. So the segmentation is a defensible cut, not a proven optimum. The dashboard shows the segment profiles so the choice can be argued instead of assumed.',
    security:
      'Runs on a public review dataset. No personal identifiers survive into the processed output.',
  },
  {
    name: 'dmqf',
    desc: 'Data-migration quality checker. Statistical profiling plus Isolation-Forest anomaly detection, AutoML-picked.',
    stack: 'Flask · PyCaret · scikit-learn',
    type: 'Live', band: 'Live',
    live: 'https://dmqf.shaily.dev',
    repo: 'SHAILY24/dmqf',
    role: 'Solo build',
    problem:
      'Migrations fail quietly. The row counts line up, the data is subtly wrong, and nobody catches it until it is already in production.',
    approach:
      'Profile source against target statistically, then run Isolation-Forest anomaly detection over the diff. PyCaret AutoML picks the detector. Flask UI on top, and the detector is a pluggable interface.',
    tradeoff:
      'AutoML is convenient and it will sometimes pick a detector that overfits one weird column. The pluggable interface is there for exactly that case, so a human can override what the AutoML chose.',
    security:
      'Profiling works off metadata and aggregate stats. It does not copy raw records, so sensitive source data stays out of the framework.',
  },
  {
    name: 'deepl-translation-poc',
    desc: 'A FastAPI + React integration over the DeepL translation API. Small on purpose.',
    stack: 'FastAPI · React',
    type: 'Live', band: 'Live',
    live: 'https://translate.shaily.dev',
    repo: 'SHAILY24/deepl-translation-poc',
    role: 'Solo build',
    problem:
      'Most third-party API examples are a one-off script that ships its key in the open. This is the deployable version of that.',
    approach:
      'FastAPI proxies the DeepL API. A small React front end talks to the proxy. The upstream key never reaches the browser.',
    tradeoff:
      'The proxy adds a hop and a little latency over calling DeepL straight from the client. I will take the latency to keep the key off the front end.',
    security:
      'The DeepL key lives only in server-side config. The front end talks to the proxy and never to DeepL.',
  },
  {
    name: 'eye-tracker-demo',
    desc: 'Gaze tracking that runs entirely in the browser. 9-point calibration. No data leaves the device.',
    stack: 'WebGazer.js · JavaScript',
    type: 'Live', band: 'Live',
    live: 'https://eyetracker.shaily.dev',
    repo: 'SHAILY24/eye-tracker-demo',
    role: 'Solo build',
    problem:
      'Gaze tracking is a fun problem. It is also a privacy problem the moment webcam frames start going to a server.',
    approach:
      'WebGazer.js, fully client-side, with a 9-point calibration step. Nothing leaves the browser tab.',
    tradeoff:
      'A webcam in a browser is less accurate than real eye-tracking hardware. For a demo I will trade the accuracy for the guarantee that nothing about your face goes anywhere.',
    security:
      'No data leaves the device. There is no back end, no upload, and camera frames are never persisted.',
  },
  // Band 2, open-source tooling & infra (code-only, no live badge)
  {
    name: 'user-port-finder',
    desc: 'A pip-installable port allocator, MIT, with a GitHub Actions workflow that publishes on tag.',
    stack: 'Python · GitHub Actions',
    type: 'Tooling', band: 'Tooling',
    live: null,
    repo: 'SHAILY24/user-port-finder',
    role: 'Open source · MIT',
    problem:
      'I self-host a lot of apps on one box. Hard-coded ports collide the moment a second service wants the same number. I got tired of fixing that by hand.',
    approach:
      'A small library that picks a free port deterministically. pip-installable, with a GitHub Actions workflow that publishes it on tag.',
    tradeoff:
      'A dependency is heavier than pasting a socket snippet into every service. It is also testable, and it behaves the same everywhere, which the snippet never did.',
    security:
      'No network calls, no privileged operations. It only checks local socket availability, so it is safe to run unprivileged in CI.',
  },
  {
    name: 'knowledge-base-rag-assistant',
    desc: 'A RAG assistant. ChromaDB and local embeddings, Claude for synthesis, React/Vite UI.',
    stack: 'FastAPI · ChromaDB · React/Vite',
    type: 'Live', band: 'Live',
    live: 'https://kbrag.shaily.dev',
    liveNote: 'retrieval demo, chat disabled',
    repo: 'SHAILY24/knowledge-base-rag-assistant',
    role: 'Solo build',
    problem:
      'A general model knows nothing about a private knowledge base it has never been shown. So it confidently makes the answer up.',
    approach:
      'Embed the corpus locally into ChromaDB, retrieve over the index, hand the scoped context to Claude to write the answer. FastAPI service, React/Vite UI.',
    tradeoff:
      'Local embeddings are a little weaker at retrieval than a hosted embedding model. For a private knowledge base I would rather keep the corpus off someone else infrastructure and eat the quality difference.',
    security:
      'The corpus is embedded and stored locally. Only the retrieved, scoped context goes to the model, never the whole knowledge base.',
  },
  {
    name: 'business-record-matcher',
    desc: 'Fuzzy entity matching with RapidFuzz multi-scorer, plus Playwright for sources stuck behind a UI.',
    stack: 'Python · RapidFuzz · Playwright',
    type: 'Tooling', band: 'Tooling',
    live: null,
    repo: 'SHAILY24/business-record-matcher',
    role: 'Open source · MIT',
    problem:
      'One business shows up as "Acme Inc", "ACME, Incorporated" and "acme inc." across three sources. An exact-match join quietly drops most of those.',
    approach:
      'Clean and normalize first, then run RapidFuzz with multiple scorers. The sources that only exist behind a web UI get scraped with Playwright.',
    tradeoff:
      'Fuzzy matching buys recall by giving up some precision. The multi-scorer setup and a tunable threshold let a reviewer pick where on that curve to sit, per dataset, instead of me guessing once.',
    security:
      'The Playwright automation respects per-source rate limits and runs on data the user supplies. No credentials are bundled in the repo.',
  },
  {
    name: 'content-engagement-analytics',
    desc: 'PostgreSQL analytics done right. Materialized views, composite indexes, a FastAPI read API. MIT.',
    stack: 'PostgreSQL · FastAPI',
    type: 'Live', band: 'Live',
    live: 'https://cea.shaily.dev',
    repo: 'SHAILY24/content-engagement-analytics',
    role: 'Open source · MIT',
    problem:
      'Engagement queries get slow the moment the event table grows. Ad-hoc SQL does not survive that, and dashboards built on it fall over.',
    approach:
      'Materialized views for the expensive aggregates, composite indexes on the hot access paths, all of it behind a FastAPI read API.',
    tradeoff:
      'Materialized views read fast but they go stale between refreshes and need a refresh strategy. For analytics, a little lag is fine. Slow queries are not.',
    security:
      'The API is read-only over pre-aggregated views. You cannot use it to mutate or pull raw event rows.',
  },
  // Band 3, domain demos & case studies
  {
    name: 'german-credit-risk-ml',
    desc: 'A credit-risk model on the German credit dataset. scikit-learn, SHAP, SMOTE.',
    stack: 'scikit-learn · SHAP · SMOTE',
    type: 'Domain', band: 'Domain',
    live: null,
    repo: 'SHAILY24/german-credit-risk-ml',
    role: 'Case study',
    problem:
      'Credit datasets skew heavily toward the good outcomes. A black-box score on top of that is hard to defend in front of a risk committee.',
    approach:
      'A scikit-learn pipeline on the German credit dataset. SMOTE for the minority class, SHAP for a per-decision explanation.',
    tradeoff:
      'SMOTE lifts minority recall by adding synthetic examples. So SHAP stays attached to every score. The explanation travels with the decision instead of being something I can dig up later.',
    security:
      'Public German credit dataset only. No real applicant data is anywhere in this.',
  },
  {
    name: 'virtual-card-auth-demo',
    desc: 'A FastAPI card-authorization pipeline running on synthetic card data.',
    stack: 'FastAPI · Python',
    type: 'Live', band: 'Live',
    live: 'https://cardauth.shaily.dev/docs',
    liveNote: 'interactive API docs',
    repo: 'SHAILY24/virtual-card-auth-demo',
    role: 'Case study',
    problem:
      'Card authorization is easy to talk about and hard to demonstrate, because demonstrating it usually means touching real card data.',
    approach:
      'A FastAPI authorization pipeline driven entirely by synthetic card data, so the whole flow runs end to end without any of that risk.',
    tradeoff:
      'Synthetic data will not hit every real-world edge case. That is fine. Real card data does not belong in a public repo, full stop.',
    security:
      'No real PAN data anywhere. The dataset is synthetic by construction, which is what makes this safe to publish at all.',
  },
  {
    name: 'databricks-banking-compliance-monitor',
    desc: 'A Databricks medallion pipeline feeding Django and Streamlit, on synthetic banking data.',
    stack: 'Databricks · Django · Streamlit',
    type: 'Domain', band: 'Domain',
    live: null,
    repo: 'SHAILY24/databricks-banking-compliance-monitor',
    role: 'Case study',
    problem:
      'Compliance monitoring off a single denormalized dump is a problem waiting to happen. It needs a clean bronze-silver-gold path you can trace.',
    approach:
      'A Databricks medallion architecture into a Django service and a Streamlit monitoring view, all on synthetic banking data.',
    tradeoff:
      'Medallion adds pipeline stages and storage over one flat table. For compliance the lineage and the audit trail you get back are the whole reason to do it.',
    security:
      'Synthetic data the whole way through. The published pipeline carries no real customer records.',
  },
  {
    name: 'banking-transaction-monitor',
    desc: 'A Spring Boot and Angular transaction monitor over a synthetic stream.',
    stack: 'Spring Boot · Angular',
    type: 'Live', band: 'Live',
    live: 'https://btxn.shaily.dev',
    liveNote: 'frontend preview',
    repo: 'SHAILY24/banking-transaction-monitor',
    role: 'Case study',
    problem:
      'Most of my public work is Python. Banks often run the JVM and Angular. This is transaction monitoring in that shape, so the work does not look stack-bound.',
    approach:
      'Spring Boot back end, Angular front end, watching a synthetic transaction stream.',
    tradeoff:
      'A second stack is more to keep alive than reusing the Python one. The point of paying that is to show the thinking carries across ecosystems and is not tied to one.',
    security:
      'Synthetic transaction data only. No real accounts or balances are represented.',
  },
  {
    name: 'mobile-bridge-poc',
    desc: 'An offline-first sync proof of concept. FastAPI and React.',
    stack: 'FastAPI · React',
    type: 'Live', band: 'Live',
    live: 'https://mbridge.shaily.dev',
    repo: 'SHAILY24/mobile-bridge-poc',
    role: 'Case study',
    problem:
      'Field tools have to keep working with zero connectivity, then reconcile cleanly the moment the network comes back. Most apps just break.',
    approach:
      'FastAPI back end, React client, an offline-first sync model that queues local changes and reconciles them on reconnect.',
    tradeoff:
      'Offline-first drags in conflict-resolution logic an online-only app never has to write. There is no way around that once the field has no signal.',
    security:
      'Sync payloads are scoped per device and the server is the reconciliation authority. A stale client cannot silently overwrite newer server state.',
  },
];

export const BAND_ORDER = { Live: 0, Tooling: 1, Domain: 2 };
export default PROJECTS;
